import React, { useState } from "react";

// Material UI components
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider
} from "@mui/material";

// Material UI icons
import {
  Dashboard,
  Apartment,
  Person,
  ExpandLess,
  ExpandMore,
  ViewList,
  AddCircleOutline,
  Update,
  Settings,
  Logout,
} from "@mui/icons-material";
import CampaignIcon from '@mui/icons-material/Campaign';
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser } from "../reducers/userReducer";
import { useNavigate } from "react-router-dom";
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupIcon from '@mui/icons-material/Group';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from "@mui/icons-material/Gavel";
import PolicyIcon from "@mui/icons-material/Policy";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ReportIcon from "@mui/icons-material/Report";



const drawerWidth = 240;

const SideNav = ({ mobileOpen, onDrawerToggle }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activePageType = queryParams.get("type");
  const [openMenus, setOpenMenus] = useState({});

  const handleMenuToggle = (menu) => {
    setOpenMenus((prevState) => ({
      ...prevState,
      [menu]: !prevState[menu],
    }));
  };

  return (
    <>
      {/* Sidebar for Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            backgroundColor: '#00152a',
            border: 'none',
            // Hide scrollbar for mobile
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
          },
        }}
      >
        <SidebarContent
          openMenus={openMenus}
          handleMenuToggle={handleMenuToggle}
          location={location}
          onDrawerToggle={onDrawerToggle}
        />
      </Drawer>

      {/* Sidebar for Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
            border: 'none',
            boxShadow: '4px 0 20px rgba(0, 0, 0, 0.3)',
            // Hide scrollbar
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
          },
        }}
        open
      >
        <SidebarContent
          openMenus={openMenus}
          handleMenuToggle={handleMenuToggle}
          location={location}
        />
      </Drawer>
    </>
  );
};

// Sidebar Content Component
const SidebarContent = ({ openMenus, handleMenuToggle, location, onDrawerToggle }) => {
  const role = useSelector((state) => state.user.role);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const queryParams = new URLSearchParams(location.search);
  const activePageType = queryParams.get("type");

  const handleConfirmLogout = () => {
    dispatch(logoutUser());
    navigate("/");
    handleClose();
  };

  // Sidebar item styles
  const menuItemStyles = {
    borderRadius: '8px',
    margin: '4px 1px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.15)',
      transform: 'translateX(4px)',
    },
  };

  const activeMenuItemStyles = {
    ...menuItemStyles,
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    borderLeft: '4px solid #3b82f6',
    '& .MuiListItemIcon-root': {
      color: '#3b82f6',
    },
    '& .MuiListItemText-primary': {
      color: '#3b82f6',
      fontWeight: '600',
    },
  };

  const iconStyle = {
    color: '#94a3b8',
    fontSize: '20px',
  };

  const activeIconStyle = {
    color: '#3b82f6',
    fontSize: '20px',
  };

  return (
    <Box sx={{
      height: '100%',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: 'white',
      // Hide scrollbar for the entire sidebar content
      overflow: 'hidden',
    }}>
      {/* Logo Section */}
      <Box sx={{
        p: 3,
        textAlign: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(15, 23, 42, 0.8)',
      }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            // background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'white',
          }}
        >
          Jemins Admin
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontSize: '0.75rem',
          }}
        >
          Management Panel
        </Typography>
      </Box>

      {/* Scrollable content without scrollbar */}
      <Box sx={{
        overflowY: 'auto',
        height: 'calc(100vh - 120px)',
        // Hide scrollbar for all browsers
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
      }}>
        {role === 'legalteam' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
            <List sx={{ flex: 1 }}>
              <ListItem
                button
                component={Link}
                onClick={() => onDrawerToggle?.()}
                to="/property/approvedPropertyGrid"
                sx={location.pathname === "/property/approvedPropertyGrid" ? activeMenuItemStyles : menuItemStyles}
              >
                <ListItemIcon>
                  <ViewList sx={location.pathname === "/property/approvedPropertyGrid" ? activeIconStyle : iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary="Approve Property"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname === "/property/approvedPropertyGrid" ? '600' : '400',
                    color: location.pathname === "/property/approvedPropertyGrid" ? '#3b82f6' : '#e2e8f0',
                  }}
                />
              </ListItem>

              <ListItem
                button
                component={Link}
                to="/property/approvedPriceUpdateGrid"
                onClick={() => onDrawerToggle?.()}
                sx={location.pathname === "/property/approvedPriceUpdateGrid" ? activeMenuItemStyles : menuItemStyles}
              >
                <ListItemIcon>
                  <ViewList sx={location.pathname === "/property/approvedPriceUpdateGrid" ? activeIconStyle : iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary="Approve Price Update"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname === "/property/approvedPriceUpdateGrid" ? '600' : '400',
                    color: location.pathname === "/property/approvedPriceUpdateGrid" ? '#3b82f6' : '#e2e8f0',
                  }}
                />
              </ListItem>

              <ListItem
                button
                component={Link}
                to='/updatesaleGrid'
                onClick={() => onDrawerToggle?.()}
                sx={location.pathname === '/updatesaleGrid' ? activeMenuItemStyles : menuItemStyles}
              >
                <ListItemIcon>
                  <ViewList sx={location.pathname === '/updatesaleGrid' ? activeIconStyle : iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary="Update Sale"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname === '/updatesaleGrid' ? '600' : '400',
                    color: location.pathname === '/updatesaleGrid' ? '#3b82f6' : '#e2e8f0',
                  }}
                />
              </ListItem>
            </List>

            {/* Logout at the bottom */}
            <List sx={{ mt: 'auto' }}>
              <ListItem
                button
                onClick={handleOpen}
                sx={menuItemStyles}
              >
                <ListItemIcon>
                  <Logout sx={iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    color: '#e2e8f0',
                  }}
                />
              </ListItem>
            </List>
          </Box>
        ) : (
          <List sx={{ p: 2 }}>
            {/* Dashboard */}
            <ListItem
              button
              component={Link}
              to="/home"
              onClick={() => onDrawerToggle?.()}
              sx={location.pathname === "/home" ? activeMenuItemStyles : menuItemStyles}
            >
              <ListItemIcon>
                <Dashboard sx={location.pathname === "/home" ? activeIconStyle : iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: location.pathname === "/home" ? '600' : '400',
                  color: location.pathname === "/home" ? '#3b82f6' : '#e2e8f0',
                }}
              />
            </ListItem>

            {/* Property Menu */}
            <ListItem
              button
              onClick={() => handleMenuToggle("property")}
              sx={menuItemStyles}
            >
              <ListItemIcon>
                <Apartment sx={iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Property"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#e2e8f0',
                }}
              />
              {openMenus.property ? <ExpandLess sx={{ color: '#94a3b8' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
            </ListItem>
            <Collapse in={openMenus.property} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {[
                  { path: "/property", text: "View Property" },
                  { path: "/apartment", text: "View Apartment" },
                  { path: "/viewProject", text: "View Project" },
                  { path: "/property/addProperty", text: "Add Property" },
                  { path: "/project/addProject", text: "Add Project" },
                  { path: "/addApartment", text: "Add Apartment" },
                  { path: "/addFlat", text: "Add Flat" },
                  { path: "/property/approvepriceupdateGrid", text: "Approve PriceUpdate" },
                ].map((item) => (
                  <ListItem
                    key={item.path}
                    button
                    component={Link}
                    onClick={() => onDrawerToggle?.()}
                    to={item.path}
                    sx={location.pathname === item.path ? {
                      ...activeMenuItemStyles,
                      pl: 4,
                    } : {
                      ...menuItemStyles,
                      pl: 4,
                    }}
                  >
                    <ListItemIcon>
                      {item.path.includes("add") ? (
                        <AddCircleOutline sx={location.pathname === item.path ? activeIconStyle : iconStyle} />
                      ) : (
                        <ViewList sx={location.pathname === item.path ? activeIconStyle : iconStyle} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === item.path ? '600' : '400',
                        color: location.pathname === item.path ? '#3b82f6' : '#cbd5e1',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>

            {/* Users Menu */}
            <ListItem
              button
              onClick={() => handleMenuToggle("investor")}
              sx={menuItemStyles}
            >
              <ListItemIcon>
                <Person sx={iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Users"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#e2e8f0',
                }}
              />
              {openMenus.investor ? <ExpandLess sx={{ color: '#94a3b8' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
            </ListItem>
            <Collapse in={openMenus.investor} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  button
                  component={Link}
                  to="/investor/investorPage"
                  onClick={() => onDrawerToggle?.()}
                  sx={location.pathname === "/investor/investorPage" ? {
                    ...activeMenuItemStyles,
                    pl: 4,
                  } : {
                    ...menuItemStyles,
                    pl: 4,
                  }}
                >
                  <ListItemIcon>
                    <ViewList sx={location.pathname === "/investor/investorPage" ? activeIconStyle : iconStyle} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Users List"
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: location.pathname === "/investor/investorPage" ? '600' : '400',
                      color: location.pathname === "/investor/investorPage" ? '#3b82f6' : '#cbd5e1',
                    }}
                  />
                </ListItem>
              </List>
            </Collapse>

            {/* Sale Menu */}
            <ListItem
              button
              onClick={() => handleMenuToggle("sale")}
              sx={menuItemStyles}
            >
              <ListItemIcon>
                <ReceiptLongIcon sx={iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Sale"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#e2e8f0',
                }}
              />
              {openMenus.sale ? <ExpandLess sx={{ color: '#94a3b8' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
            </ListItem>
            <Collapse in={openMenus.sale} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem
                  button
                  component={Link}
                  onClick={() => onDrawerToggle?.()}
                  to="/investor/sale"
                  sx={location.pathname === "/investor/sale" ? {
                    ...activeMenuItemStyles,
                    pl: 4,
                  } : {
                    ...menuItemStyles,
                    pl: 4,
                  }}
                >
                  <ListItemIcon>
                    <ViewList sx={location.pathname === "/investor/sale" ? activeIconStyle : iconStyle} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Update Sale"
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: location.pathname === "/investor/sale" ? '600' : '400',
                      color: location.pathname === "/investor/sale" ? '#3b82f6' : '#cbd5e1',
                    }}
                  />
                </ListItem>
              </List>
            </Collapse>

            {/* Single menu items */}
            {[
              // { path: "/kyp", text: "KYC", icon: VerifiedUserIcon },
              { path: "/team", text: "Team", icon: GroupIcon },
              { path: "/wallet", text: "Wallet", icon: AccountBalanceWalletIcon },
              { path: "/admin/Advertisement", text: "AddAdvertisement", icon: CampaignIcon },
              { path: "/admin/subscription", text: "Subscription", icon: SubscriptionsIcon },
            ].map((item) => (
              <ListItem
                key={item.path}
                button
                component={Link}
                to={item.path}
                onClick={() => onDrawerToggle?.()}
                sx={location.pathname === item.path ? activeMenuItemStyles : menuItemStyles}
              >
                <ListItemIcon>
                  <item.icon sx={location.pathname === item.path ? activeIconStyle : iconStyle} />
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: location.pathname === item.path ? '600' : '400',
                    color: location.pathname === item.path ? '#3b82f6' : '#e2e8f0',
                  }}
                />
              </ListItem>
            ))}



            {/* Report Menu */}
            <ListItem
              button
              onClick={() => handleMenuToggle("report")}
              sx={menuItemStyles}
            >
              <ListItemIcon>
                <AssessmentIcon sx={iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Report"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#e2e8f0',
                }}
              />
              {openMenus.report ? <ExpandLess sx={{ color: '#94a3b8' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
            </ListItem>
            <Collapse in={openMenus.report} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {[
                  { path: "/report/enquiries", text: "Enquiries" },
                  { path: "/report/sitevisit", text: "Site Visit" },
                  { path: "/BookedProperties", text: "Booked Properties" },
                  // { path: "/DocumentRequests", text: "Document Requests" },
                ].map((item) => (
                  <ListItem
                    key={item.path}
                    button
                    component={Link}
                    onClick={() => onDrawerToggle?.()}
                    to={item.path}
                    sx={location.pathname === item.path ? {
                      ...activeMenuItemStyles,
                      pl: 4,
                    } : {
                      ...menuItemStyles,
                      pl: 4,
                    }}
                  >
                    <ListItemIcon>
                      <ViewList sx={location.pathname === item.path ? activeIconStyle : iconStyle} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === item.path ? '600' : '400',
                        color: location.pathname === item.path ? '#3b82f6' : '#cbd5e1',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>

            {/* Settings Menu */}
            <ListItem
              button
              onClick={() => handleMenuToggle("settings")}
              sx={menuItemStyles}
            >
              <ListItemIcon>
                <Settings sx={iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#e2e8f0',
                }}
              />
              {openMenus.settings ? <ExpandLess sx={{ color: '#94a3b8' }} /> : <ExpandMore sx={{ color: '#94a3b8' }} />}
            </ListItem>

            <Collapse in={openMenus.settings} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {[
                  { path: "/settings/MobileTheme", text: "MobileThemes" },

                  { path: "/addsubscriptionplans", text: "SubscriptionPlans" },
                  { path: "/setsharepercentage", text: "Share Percentage" },
                  { path: "/website/themecolors", text: "Website Themes" },
                  { path: "/settings/general", text: "General" },
                  { path: "/settings/FAQList", text: "FAQs" },
                ].map((item) => (
                  <ListItem
                    key={item.path}
                    button
                    component={Link}
                    onClick={() => onDrawerToggle?.()}
                    to={item.path}
                    sx={location.pathname === item.path ? {
                      ...activeMenuItemStyles,
                      pl: 4,
                    } : {
                      ...menuItemStyles,
                      pl: 4,
                    }}
                  >
                    <ListItemIcon>
                      <ViewList sx={location.pathname === item.path ? activeIconStyle : iconStyle} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === item.path ? '600' : '400',
                        color: location.pathname === item.path ? '#3b82f6' : '#cbd5e1',
                      }}
                    />
                  </ListItem>
                ))}
              </List>

            </Collapse>
            {/* Footer Pages */}
            {/* <ListItem
              button
              onClick={() => handleMenuToggle("footer")}
              sx={menuItemStyles}
            >
              <ListItemIcon>
                <GavelIcon sx={iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Footer Pages"
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  color: "#e2e8f0",
                }}
              />
              {openMenus.footer ? (
                <ExpandLess sx={{ color: "#94a3b8" }} />
              ) : (
                <ExpandMore sx={{ color: "#94a3b8" }} />
              )}
            </ListItem> */}

            <Collapse in={openMenus.footer} timeout="auto" unmountOnExit>
  <List component="div" disablePadding>
    {[
      { path: "/admin/pages?type=terms", text: "Terms & Conditions", icon: GavelIcon, type: "terms" },
      { path: "/admin/pages?type=privacy", text: "Privacy Policy", icon: PolicyIcon, type: "privacy" },
      { path: "/admin/pages?type=refund", text: "Refund Policy", icon: AssignmentReturnIcon, type: "refund" },
      { path: "/admin/pages?type=disclaimer", text: "Disclaimer", icon: ReportIcon, type: "disclaimer" },
    ].map((item) => {
      const isActive = activePageType === item.type;

      return (
        <ListItem
          key={item.path}
          button
          component={Link}
          to={item.path}
          onClick={() => onDrawerToggle?.()}
          sx={{
            ...(isActive ? activeMenuItemStyles : menuItemStyles),
            pl: 4,
          }}
        >
          <ListItemIcon>
            <item.icon sx={isActive ? activeIconStyle : iconStyle} />
          </ListItemIcon>

          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: "0.85rem",
              fontWeight: isActive ? "600" : "400",
              color: isActive ? "#3b82f6" : "#cbd5e1",
            }}
          />
        </ListItem>
      );
    })}
  </List>
</Collapse>


            {/* Logout */}
            <ListItem
              button
              onClick={handleOpen}
              sx={{
                ...menuItemStyles,
                marginTop: 'auto',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '16px',
              }}
            >
              <ListItemIcon>
                <Logout sx={iconStyle} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#e2e8f0',
                }}
              />
            </ListItem>
          </List>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            width: '400px',
            maxWidth: '90vw'
          }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #004ba0 100%)',
          color: 'white',
          p: 2,
          textAlign: 'center'
        }}>
          <DialogTitle sx={{
            color: 'inherit',
            fontWeight: 600,
            fontSize: '1.5rem',
            py: 1
          }}>
            Confirm Logout
          </DialogTitle>
        </Box>

        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'warning.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </Box>
          </Box>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
            Ready to leave?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to logout from your account?
          </Typography>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          justifyContent: 'center',
          gap: 2,
          pt: 0
        }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            color="error"
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              background: 'linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)',
              boxShadow: '0 4px 10px rgba(211, 47, 47, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                boxShadow: '0 6px 14px rgba(211, 47, 47, 0.3)'
              }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SideNav;